import type { ReactNode } from 'react'
import { Heading, Paragraph, type HeadingProps } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// EmptyState
//
// Presentational centered empty-state panel. Renders when a list or view has
// no content to display. DS owns typography; the .empty family keeps layout.
//
// API (contracts/component-library.md):
//   icon?     — IconName; decorative icon displayed above the title
//   title     — required; primary message (already i18n'd by the caller)
//   subtitle? — optional explanatory line rendered below the title
//   action?   — optional ReactNode slot; caller renders a Button or link
//
// CSS classes:
//   .empty         — flex column, centred, 60px vertical padding
//   .empty__icon   — 48×48 wrapper, 50% opacity, 12px bottom margin
//   .empty__title  — 15px semibold heading
//   .empty__sub    — 12.5px muted note
//
// No data fetching, no privileged logic — safe for the public register bundle.
// ---------------------------------------------------------------------------

export type EmptyStateProps = {
  /** Decorative icon name from the shared Icon registry. */
  icon?: IconName
  /** Primary empty-state message. Caller provides an already-i18n'd string. */
  title: string
  /** Lists default to h2; standalone error pages can opt into h1. */
  headingLevel?: HeadingProps['level']
  /** Optional supporting explanation rendered below the title. */
  subtitle?: string
  /**
   * Optional action slot. Caller composes a Button, link, or other control.
   * Rendered after the subtitle so it is naturally last in reading order.
   */
  action?: ReactNode
}

/**
 * Centred empty-state panel. Use when a list or data region has nothing to
 * display — show why, and (optionally) offer a way forward via the action slot.
 *
 * Usage:
 *   <EmptyState
 *     icon="inbox"
 *     title={t('cases.empty.title')}
 *     subtitle={t('cases.empty.subtitle')}
 *     action={<Button variant="primary" onClick={onNew}>{t('cases.empty.action')}</Button>}
 *   />
 */
export function EmptyState({ icon, title, headingLevel = 2, subtitle, action }: EmptyStateProps) {
  return (
    <div className="empty">
      {icon !== undefined && (
        <div className="empty__icon">
          {/* Icon is decorative — the title conveys the meaning */}
          <Icon name={icon} size={48} aria-hidden="true" />
        </div>
      )}

      <Heading level={headingLevel} data-size="2xs" className="empty__title">{title}</Heading>

      {subtitle !== undefined && (
        <Paragraph data-size="sm" className="empty__sub">{subtitle}</Paragraph>
      )}

      {action}
    </div>
  )
}
